import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Smartphone, Download, Star, Heart, Zap } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [_canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
    
    // Check user's prompt history
    const promptDismissed = localStorage.getItem('pwa-prompt-dismissed');
    const remindLaterTime = localStorage.getItem('pwa-remind-later');
    const isInstalled = localStorage.getItem('pwa-installed');
    
    // Don't show if already installed
    if (isInstalled === 'true') return;
    
    // Check if user dismissed permanently or if we should wait
    const now = Date.now();
    let shouldShow = true;
    
    if (promptDismissed) {
      const dismissedTime = parseInt(promptDismissed);
      const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours
      
      // Only show again after 1 day if they dismissed it
      if (now - dismissedTime < oneDayInMs) {
        shouldShow = false;
      }
    }
    
    if (remindLaterTime) {
      const remindTime = parseInt(remindLaterTime);
      // Don't show until remind time has passed (7 days for "remind later")
      if (now < remindTime) {
        shouldShow = false;
      }
    }
    
    // Listen for the PWA install prompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
      
      // Show our custom prompt if all conditions are met
      if (!isStandalone && shouldShow) {
        // Wait a bit before showing to let user get familiar with the app
        setTimeout(() => {
          setShowPrompt(true);
        }, 30000); // Show after 30 seconds
      }
    };

    // Listen for successful app installation
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
      setCanInstall(false);
      localStorage.setItem('pwa-installed', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback for iOS or browsers without native prompt
      setShowPrompt(false);
      showManualInstallInstructions();
      return;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the PWA install prompt');
        localStorage.setItem('pwa-installed', 'true');
      } else {
        console.log('User dismissed the PWA install prompt');
        localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
      }
      
      setShowPrompt(false);
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error during PWA installation:', error);
      showManualInstallInstructions();
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Set dismiss time for 24-hour cooldown
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    // Clear any existing remind-later setting
    localStorage.removeItem('pwa-remind-later');
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
    // Show again in 7 days (industry standard for non-intrusive reminders)
    const remindTime = Date.now() + (7 * 24 * 60 * 60 * 1000);
    localStorage.setItem('pwa-remind-later', remindTime.toString());
    // Clear any existing dismiss setting since they chose remind later
    localStorage.removeItem('pwa-prompt-dismissed');
  };

  const showManualInstallInstructions = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    let instructions = '';
    if (isIOS) {
      instructions = 'Tap the Share button (⎹⤴) in Safari, then "Add to Home Screen"';
    } else if (isAndroid) {
      instructions = 'Tap the menu (⋮) in your browser, then "Add to Home screen" or "Install app"';
    } else {
      instructions = 'Look for the install button in your browser\'s address bar, or check your browser menu for "Install" or "Add to Home Screen"';
    }
    
    alert(`To install Friendships Vibe:\n\n${instructions}`);
  };

  const getBrowserSpecificInstructions = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    if (isIOS) {
      return {
        icon: '⎹⤴',
        text: 'Tap Share → Add to Home Screen',
      };
    } else if (isAndroid) {
      return {
        icon: '⋮',
        text: 'Tap Menu → Install App',
      };
    } else {
      return {
        icon: '📱',
        text: 'Look for Install button in browser',
      };
    }
  };

  if (!showPrompt) return null;

  const browserInstructions = getBrowserSpecificInstructions();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 50 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="relative w-full max-w-md bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-3xl border border-purple-500/30 overflow-hidden shadow-2xl"
        >
          {/* Close Button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/20 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-black/40 transition-all"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Header with Tess */}
          <div className="relative p-6 text-center">
            {/* Background glow effect */}
            <div className="absolute inset-0 bg-gradient-to-b from-purple-500/20 to-transparent" />
            
            <div className="relative">
              {/* Tess Image */}
              <motion.div
                initial={{ scale: 0, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden ring-4 ring-purple-400/50 shadow-lg"
              >
                <img
                  src="/images/friendship-app-promo.png"
                  alt="Friendships Vibe App"
                  className="w-full h-full object-cover"
                />
              </motion.div>

              {/* Speech Bubble */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20"
              >
                {/* Speech bubble tail */}
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                  <div className="w-4 h-4 bg-white/10 border-l border-t border-white/20 rotate-45 backdrop-blur-sm" />
                </div>
                
                <div className="text-base sm:text-lg font-semibold text-white mb-1">
                  Level up your friendships!
                </div>
                <div className="text-purple-200 text-xs sm:text-sm">
                  &quot;Save Friendships Vibe to your home screen for instant access to your friendship-building journey!&quot;
                </div>
              </motion.div>
            </div>
          </div>

          {/* Benefits */}
          <div className="px-6 py-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              {[
                { icon: Zap, text: 'Instant Access', color: 'text-yellow-400' },
                { icon: Heart, text: 'No Browsers', color: 'text-pink-400' },
                { icon: Star, text: 'App Experience', color: 'text-purple-400' },
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="p-3 bg-white/5 rounded-xl border border-white/10"
                >
                  <benefit.icon className={`w-5 h-5 ${benefit.color} mx-auto mb-1`} />
                  <div className="text-white text-xs font-medium">{benefit.text}</div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-6 space-y-3">
            {/* Primary Install Button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              onClick={handleInstallClick}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Download className="w-5 h-5" />
              <span>Add to Home Screen</span>
              <Smartphone className="w-5 h-5" />
            </motion.button>

            {/* Manual Instructions (for iOS/fallback) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="text-center p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl"
            >
              <div className="text-blue-300 text-sm font-medium mb-1">
                Can&apos;t see install option?
              </div>
              <div className="text-blue-200 text-xs flex items-center justify-center gap-2">
                <span className="text-lg">{browserInstructions.icon}</span>
                <span>{browserInstructions.text}</span>
              </div>
            </motion.div>

            {/* Secondary Action */}
            <button
              onClick={handleRemindLater}
              className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 px-4 rounded-lg transition-all text-sm font-medium"
            >
              Remind Me Later
            </button>
          </div>

          {/* Footer Note */}
          <div className="px-6 pb-6">
            <div className="text-center text-xs text-slate-400">
              Free whilst in Beta • Works offline • No storage limits
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;